import { Settings } from "../context/SettingsContext/SettingsContext";
import { getCalculatedPrice } from "../styles/Price/useCalculatedPrice";
import firebase, { getRegion } from "./firebase";

const apiHost = "https://services.mobile.de";
const region = getRegion();

export interface SearchParams {
  page?: number;
  brandRef?: string;
  modelRef?: string;
  priceMin?: number;
  priceMax?: number;
  minConstructionYear?: number;
  maxConstructionYear?: number;
  minMileage?: number;
  maxMileage?: number;
  gearbox?: string[];
  sort?: string;
  exteriorColor?: string[];
  fuelType?: string[];
  interiorType?: string[];
  interiorColor?: string[];
  feature?: string[];
  category?: string[];
  vatable?: "" | "1" | "0";
}

export interface Car {
  id: number | string;
  price: number | null;
  vatable: boolean;
  images: string[];
  brand: string | null;
  model: string | null;
  modelDescription: string | null;
  specifics: {
    fuel?: string | null;
    gearbox?: string | null;
    "construction-year"?: string | null;
    "first-registration": string;
    mileage: string | null;
    "emission-fuel-consumption"?: number;
    [k: string]: any;
  };
  features: Array<{
    key: string | null;
    value: string | null;
  }>;
}

export interface Model {
  ref: string;
  name: string;
}

export interface Brand {
  name: string;
  ref: string;
}

export interface FuelType {
  name: string;
  key: string;
}

export interface InteriorColor {
  name: string;
  key: string;
}

export interface InteriorType {
  name: string;
  key: string;
}

export interface Feature {
  name: string;
  key: string;
}

export interface Category {
  name: string;
  key: string;
}

export interface Pagination {
  total: number;
  currentPage: number;
  maxPages: number;
}

interface FetchCarsResponse {
  cars: Car[];
  pagination: Pagination;
}

export const fetchCars = async (
  form: SearchParams,
  settings: Settings
): Promise<FetchCarsResponse | false> => {
  const params = new URLSearchParams();
  params.append("country", "DE");
  params.append("page.number", form.page ? form.page.toString() : "1");

  params.append("sellerType", "DEALER");
  if (form.modelRef) {
    params.append("classification", form.modelRef.replace(`${apiHost}/`, ""));
  }

  if (form.priceMin) {
    params.append("price.min", form.priceMin.toString());
  }

  if (form.priceMax) {
    params.append("price.max", form.priceMax.toString());
  }

  if (form.minConstructionYear) {
    params.append("constructionYear.min", form.minConstructionYear.toString());
  }

  if (form.maxConstructionYear) {
    params.append("constructionYear.max", form.maxConstructionYear.toString());
  }

  if (form.minMileage) {
    params.append("mileage.min", `${form.minMileage}`);
  }

  if (form.maxMileage) {
    params.append("mileage.max", `${form.maxMileage}`);
  }

  if (form.gearbox && form.gearbox.length > 0) {
    params.append("gearbox", form.gearbox.join(","));
  }

  if (form.exteriorColor && form.exteriorColor.length > 0) {
    params.append("exteriorColor", form.exteriorColor.join(","));
  }

  if (form.fuelType && form.fuelType.length > 0) {
    params.append("fuel", form.fuelType.join(","));
  }

  if (form.interiorColor && form.interiorColor.length > 0) {
    params.append("interiorColor", form.interiorColor.join(","));
  }

  if (form.interiorType && form.interiorType.length > 0) {
    params.append("interiorType", form.interiorType.join(","));
  }

  if (form.feature && form.feature.length > 0) {
    params.append("feature", form.feature.join(","));
  }

  if (form.category && form.category.length > 0) {
    params.append("category", form.category.join(","));
  }

  if (form.vatable === "0" || form.vatable === "1") {
    params.append("vatable", form.vatable);
  }

  if (form.sort) {
    const [sortField, sortOrder] = form.sort.split("_");
    params.append("sort.field", sortField);
    params.append("sort.order", sortOrder);
  }

  try {
    const callSearch = firebase
      .app()
      .functions(region)
      .httpsCallable("callSearch") as (
      params: string
    ) => Promise<{
      data: {
        results: Car[];
        pagination: Pagination;
      };
    }>;
    const response = await callSearch(params.toString());
    let cars = response.data.results
      .map((car) => ({
        ...car,
        price: getCalculatedPrice(car, settings),
      }))
      .filter((car) => car.price !== null);
    if (form.sort === "price_ASCENDING") {
      cars = cars.sort((carA, carB) => {
        if (carA.price === null) {
          return 1;
        }
        if (carB.price === null) {
          return -1;
        }
        return carA.price - carB.price;
      });
    } else if (form.sort === "price_DESCENDING") {
      cars = cars.sort((carA, carB) => {
        if (carA.price === null) {
          return 1;
        }
        if (carB.price === null) {
          return -1;
        }
        return carB.price - carA.price;
      });
    }
    return {
      cars,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error(error);
    return false;
  }
};

interface FetchCarResponse {
  car: Car;
}

export const fetchCar = async (
  id: number,
  settings: Settings
): Promise<FetchCarResponse | false> => {
  try {
    const callGet = firebase
      .app()
      .functions(region)
      .httpsCallable("callGet") as (
      id: number
    ) => Promise<{
      data: Car;
    }>;
    const response = await callGet(id);
    return {
      car: {
        ...response.data,
        price: getCalculatedPrice(response.data, settings),
      },
    };
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const fetchBrands = async () => {
  try {
    const callBrands = firebase
      .app()
      .functions(region)
      .httpsCallable("callBrands") as () => Promise<{ data: Brand[] }>;
    const brands = await callBrands();
    return brands.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};
export const fetchFuelTypes = async () => {
  try {
    const callFuelTypes = firebase
      .app()
      .functions(region)
      .httpsCallable("callFuelTypes") as () => Promise<{ data: FuelType[] }>;
    const fuelTypes = await callFuelTypes();
    return fuelTypes.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const fetchInteriorColors = async () => {
  try {
    const callInteriorColors = firebase
      .app()
      .functions(region)
      .httpsCallable("callInteriorColors") as () => Promise<{
      data: InteriorColor[];
    }>;
    const interiorColors = await callInteriorColors();
    return interiorColors.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const fetchInteriorTypes = async () => {
  try {
    const callInteriorTypes = firebase
      .app()
      .functions(region)
      .httpsCallable("callInteriorTypes") as () => Promise<{
      data: InteriorType[];
    }>;
    const interiorTypes = await callInteriorTypes();
    return interiorTypes.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const fetchFeatures = async () => {
  try {
    const callFeatures = firebase
      .app()
      .functions(region)
      .httpsCallable("callFeatures") as () => Promise<{ data: Feature[] }>;
    const features = await callFeatures();
    return features.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const fetchCategories = async () => {
  try {
    const callCategories = firebase
      .app()
      .functions(region)
      .httpsCallable("callCategories") as () => Promise<{ data: FuelType[] }>;
    const categories = await callCategories();
    return categories.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

interface FetchModelsResponse {
  models: Model[];
}

export const fetchModels = async (
  brandRef: string
): Promise<FetchModelsResponse | false> => {
  try {
    const callModels = firebase
      .app()
      .functions(region)
      .httpsCallable("callModels") as (
      brandRef: string
    ) => Promise<{ data: Model[] }>;
    const response = await callModels(brandRef);
    return { models: response.data };
  } catch (error) {
    console.error(error);
    return false;
  }
};
